import React from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Toolbar, Button, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import CodeIcon from '@mui/icons-material/Code';
import CampaignIcon from '@mui/icons-material/Campaign';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ShareIcon from '@mui/icons-material/Share';
import TimelineIcon from '@mui/icons-material/Timeline';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarIcon from '@mui/icons-material/Star';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PollIcon from '@mui/icons-material/Poll';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import featureFlags from '../../config/featureFlags';
import usePermissions from '../../hooks/usePermissions';

const drawerWidth = 240;


const Sidebar = ({ mobileOpen, handleDrawerToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeProject } = useProject();
  const { isOrgAdmin, isOrgOwner } = usePermissions();

  // Use activeProject from context instead of URL
  const projectId = activeProject?.id || null;

  const generalItems = [
    featureFlags.showActivityFeed ? { text: 'Activity Feed', icon: <RssFeedIcon />, path: '/feed' } : null,
    {
      text: 'Public Roadmap',
      icon: <TimelineIcon />,
      path: projectId ? `/roadmap/${projectId}` : '/roadmap'
    },
  ].filter(Boolean);

  const projectSpecificItems = projectId ? [
    {
      text: 'Overview',
      icon: <CodeIcon />,
      path: `/projects/${projectId}`,
      exact: true
    },
    {
      text: 'Features',
      icon: <CampaignIcon />,
      path: `/projects/${projectId}/features`,
      exact: false
    },
    {
      text: 'Requirements',
      icon: <AssignmentIcon />,
      path: `/projects/${projectId}/requirements`,
      exact: false
    },
    {
      text: 'Design Docs',
      icon: <DescriptionIcon />,
      path: `/projects/${projectId}/design-documents`,
      exact: false
    },
    featureFlags.showWorkboard ? {
      text: 'Workboard',
      icon: <ViewKanbanIcon />,
      path: `/projects/${projectId}/workboard`,
      exact: false
    } : null,
    featureFlags.showReleases ? {
      text: 'Releases',
      icon: <RocketLaunchIcon />,
      path: `/projects/${projectId}/releases`,
      exact: false
    } : null,
  ].filter(Boolean) : [];

  const managementItems = [
    { text: 'Settings', icon: <SettingsIcon />, path: '/organization/settings' },
    { text: 'Atlassian', icon: <CloudSyncIcon />, path: '/settings/atlassian' },
  ];

  const billingItems = [
    { text: 'Billing & Usage', icon: <CreditCardIcon />, path: '/billing' },
    { text: 'Upgrade Plan', icon: <StarIcon />, path: '/pricing' },
  ];

  const socialMediaItems = [
    { text: 'Social Media', icon: <ThumbUpIcon />, path: '/social-media' },
    { text: 'Posts', icon: <ShareIcon />, path: '/social-media/posts' },
    { text: 'Compose', icon: <AddIcon />, path: '/social-media/compose' },
  ];

  const surveyItems = [
    { text: 'Survey Templates', icon: <AssignmentIcon />, path: '/survey-templates' },
    { text: 'Surveys', icon: <PollIcon />, path: '/surveys' },
  ];


  const drawer = (
    <div data-tutorial-id="sidebar-drawer">
      <Toolbar />
      <Divider />
      <List>
        {generalItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
            sx={{
              '& .MuiListItemButton-root': {
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                },
              },
            }}
          >
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={item.exact ? pathname === item.path : pathname.startsWith(item.path)}
            >
              <ListItemIcon>
                {React.cloneElement(item.icon, {
                  color: pathname.startsWith(item.path) ? 'primary' : 'inherit',
                })}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {projectSpecificItems.length > 0 && <Divider>Project</Divider>}
      <List>
        {projectSpecificItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
            sx={{
              '& .MuiListItemButton-root': {
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                },
              },
            }}
          >
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={item.exact ? pathname === item.path : pathname.startsWith(item.path)}
            >
              <ListItemIcon>
                {React.cloneElement(item.icon, {
                  color: pathname.startsWith(item.path) ? 'primary' : 'inherit',
                })}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ px: 2, pb: 2 }}>
        {projectId ? (
          <Tooltip title="Create a new feature for this project — define its description, requirements, and track it through to delivery" placement="right">
            <Button
              fullWidth
              data-tutorial-id="new-feature-button"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/projects/${projectId}/features/new`)}
              sx={{ justifyContent: 'flex-start' }}
            >
              New Feature
            </Button>
          </Tooltip>
        ) : (
          <Tooltip title="Create a new project to start organizing your features, requirements, and roadmap" placement="right">
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/projects')}
              sx={{ justifyContent: 'flex-start' }}
            >
              Create Project
            </Button>
          </Tooltip>
        )}
      </Box>
      <Divider>Marketing</Divider>
      <List>
        {socialMediaItems
          .filter((item) => item.text !== 'Compose')
          .map((item) => (
            <ListItem
              key={item.text}
              disablePadding
              sx={{
                '& .MuiListItemButton-root': {
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  },
                },
              }}
            >
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={item.exact ? pathname === item.path : pathname.startsWith(item.path)}
                data-tutorial-id={
                  item.text === 'Social Media' ? 'social-media-nav' :
                  item.text === 'Requirements' ? 'requirements-nav' :
                  item.text === 'Design Docs' ? 'design-docs-nav' :
                  undefined
                }
              >
                <ListItemIcon>
                  {React.cloneElement(item.icon, {
                    color: pathname.startsWith(item.path) ? 'primary' : 'inherit',
                  })}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
      <Box sx={{ px: 2, pb: 2 }}>
        <Tooltip title="Write and schedule a new social media post across your connected platforms" placement="right">
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/social-media/compose')}
            data-tutorial-id="compose-button"
            sx={{ justifyContent: 'flex-start' }}
          >
            Compose
          </Button>
        </Tooltip>
      </Box>
      <Divider>Customer Feedback</Divider>
      <List>
        {surveyItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
            sx={{
              '& .MuiListItemButton-root': {
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                },
              },
            }}
          >
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={item.exact ? pathname === item.path : pathname.startsWith(item.path)}
            >
              <ListItemIcon>
                {React.cloneElement(item.icon, {
                  color: pathname.startsWith(item.path) ? 'primary' : 'inherit',
                })}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ px: 2, pb: 2 }}>
        <Tooltip title="Create a new customer feedback survey to collect insights about your features and product" placement="right">
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/surveys/create')}
            sx={{ justifyContent: 'flex-start' }}
          >
            New Survey
          </Button>
        </Tooltip>
      </Box>
      {isOrgOwner && (
        <>
          <Divider>Billing</Divider>
          <List>
            {billingItems.map((item) => (
              <ListItem
                key={item.text}
                disablePadding
                sx={{
                  '& .MuiListItemButton-root': {
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.action.selected,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    },
                  },
                }}
              >
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={item.exact ? pathname === item.path : pathname.startsWith(item.path)}
                >
                  <ListItemIcon>
                    {React.cloneElement(item.icon, {
                      color: pathname.startsWith(item.path) ? 'primary' : 'inherit',
                    })}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {isOrgAdmin && (
        <>
          <Divider>Management</Divider>
          <List>
            {managementItems.map((item) => (
              <ListItem
                key={item.text}
                disablePadding
                sx={{
                  '& .MuiListItemButton-root': {
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.action.selected,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    },
                  },
                }}
              >
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={item.exact ? pathname === item.path : pathname.startsWith(item.path)}
                  data-tutorial-id={item.text === 'Atlassian' ? 'atlassian-nav' : undefined}
                >
                  <ListItemIcon>
                    {React.cloneElement(item.icon, {
                      color: pathname.startsWith(item.path) ? 'primary' : 'inherit',
                    })}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label="mailbox folders"
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
